/**
 * AUTOPILOT
 *
 * Pathfinding flight assist. Caller passes world-space (x, y); we A* through a
 * 100u grid that has mountain walls rasterized as blocked cells, then send
 * UP/LEFT/RIGHT/SPECIAL key events at 100ms cadence to steer the ship there.
 *
 * Cancels on:
 *   - Arrival at the final node
 *   - The player taking over: any direction or special press, from the
 *     keyboard, gamepad, touch or mouse mode (reported by Input.ts). Firing
 *     doesn't cancel, so you can shoot while it flies you, as in SWAM.
 *   - Death / teleport (player jumped > 300u in 100ms)
 *   - Player explicitly via UI / cancel button
 *
 * Modeled on SWAM's AutoPilot: its 100 ms loop, 300-unit teleport test and
 * Predator boost rule (energy above 0.8, more than 500 units to go, 1.5 s)
 * are kept; its thrust cut above 0.75 rad of turn and 250 ms turn cooldown
 * are not, replaced by look-ahead steering with thrust held and a 50 ms
 * cooldown. Mimic-target / mimic-keystroke behavior is deliberately NOT
 * ported (cheat-adjacent).
 *
 * 100ms cadence is intentional — matches SWAM and keeps key-event traffic low
 * enough that the server doesn't flag as flooding.
 */

import { PLAYER_STATUS } from '@airmash/protocol';
import { uiState } from '../ui/UIState';
import { MAP_CENTER_X, MAP_CENTER_Y, MAP_HEIGHT, MAP_WIDTH, PlaneType, TWO_PI } from './Constants';
import { SHIPS } from './GameData';
import { walls } from './doodads/DoodadData';
import { keyState as inputKeyState } from './Input';
import { sendKey } from './Network';
import { Pathfinder, unpackX, unpackY } from './pathfinding/Pathfinder';
import { getMe } from './Players';
import type { BindName, KeyName } from './types/Input';

const CELL_SIZE = 100;
const GRID_W = Math.ceil(MAP_WIDTH / CELL_SIZE);
const GRID_H = Math.ceil(MAP_HEIGHT / CELL_SIZE);

/** Tick cadence — paced to avoid server flood-detection (matches SWAM). */
const TICK_MS = 100;

/** Distance below which we count as "arrived" at the current waypoint. */
const ARRIVAL_DIST = 100;
const ARRIVAL_DIST_SQ = ARRIVAL_DIST * ARRIVAL_DIST;

/** Final-destination arrival threshold (looser to avoid orbiting the goal). */
const FINAL_ARRIVAL_DIST = 200;
const FINAL_ARRIVAL_DIST_SQ = FINAL_ARRIVAL_DIST * FINAL_ARRIVAL_DIST;

/**
 * Per-100ms pos-delta that signals teleport / respawn / death. Scaled by
 * actual elapsed time per tick so a delayed setInterval (background tab,
 * GC pause) doesn't false-cancel during a legitimate boost.
 */
const TELEPORT_DELTA_PER_100MS = 300;

/** Angle (radians) above which we issue a turn pulse. */
const TURN_THRESHOLD = 0.1;

/**
 * Pure-pursuit look-ahead distance. Steering targets a point this far ahead
 * along the path instead of the immediate next waypoint, so corners arc
 * smoothly: by the time the plane is "pointing at the corner" the look-ahead
 * point has already slid past it onto the outgoing segment, the steering
 * input becomes a gentle bank, and the plane never has to spin in place to
 * realign with a maze entrance. Tuned in world units (path cells are 100u).
 *
 * Trade-off vs. wall clearance: larger values arc more aggressively across
 * corners, increasing how far the actual flight path deviates inward from
 * the planned path — at sharp turns near walls that deviation can clip the
 * mountain. We can't compensate with configuration-space inflation (rasterizing
 * walls bigger than they are) because typical maze corridors are only ~2 cells
 * wide and any padding pushes A* to refuse them. So this stays the primary
 * dial: lower to track the path more tightly, raise for smoother corners.
 */
const LOOKAHEAD_DIST = 400;

/**
 * Per-cell A* cost added when entering a cell adjacent to any blocked cell.
 * Biases the planned path away from skimming wall edges so the plane (which is
 * wider than a cell center) doesn't clip when tracking the path. Threading
 * tight passages with no alternative is unaffected — the penalty just adds a
 * constant to every cell on both routes, so the threading still wins. Tune up
 * for more clearance from mountains, down if A* starts taking visibly longer
 * detours.
 */
const NEAR_WALL_PENALTY = 0.5;

/** Minimum / maximum turn-pulse durations (ms). Caps stop us committing to
 * a turn so long the path moves underneath us before re-evaluation. */
const TURN_PULSE_MIN_MS = 30;
const TURN_PULSE_MAX_MS = 200;

/** Cooldown after a turn pulse before we accept another (ms). */
const TURN_COOLDOWN_MS = 50;

/** Minimum energy fraction to attempt a Predator boost. */
const BOOST_ENERGY = 0.8;

/** Distance beyond which boost is worth using. */
const BOOST_DIST = 500;
const BOOST_DIST_SQ = BOOST_DIST * BOOST_DIST;

/** Boost burst duration (ms). */
const BOOST_MS = 1500;

let pathfinder: Pathfinder | null = null;
let active = false;
let path: Uint32Array | null = null;
let nodeIndex = 0;
let prevX = 0;
let prevY = 0;
let prevTickTime = 0;
let intervalId: number | null = null;
let boostUntil = 0;

let rotating = false;
let turnReleaseTimerId: number | null = null;
let turnCooldownTimerId: number | null = null;

function clearTurnTimers(): void {
  if (turnReleaseTimerId !== null) {
    clearTimeout(turnReleaseTimerId);
    turnReleaseTimerId = null;
  }
  if (turnCooldownTimerId !== null) {
    clearTimeout(turnCooldownTimerId);
    turnCooldownTimerId = null;
  }
  rotating = false;
}

/** The four keys the autopilot ever sends, and whether it holds each. */
const heldKeys = { UP: false, LEFT: false, RIGHT: false, SPECIAL: false };
type HeldKey = keyof typeof heldKeys;

function isHeldKey(key: KeyName): key is HeldKey {
  return key in heldKeys;
}

/**
 * The autopilot and the player's input (Input.ts) both drive the server's key
 * state, each skipping sends its own record says are repeats. So the server
 * sees a key down while either of them holds it: a release from one side
 * leaves the key down if the other still holds it. Here that means cruise's
 * UP, or a key held since before the autopilot started, survives its cancel;
 * Input.ts holds back the player's release of a key held here.
 */
function pressKey(key: HeldKey, pressed: boolean): void {
  if (heldKeys[key] === pressed) return;
  heldKeys[key] = pressed;
  if (!pressed && inputKeyState(key)) return;
  sendKey(key, pressed);
}

/** Whether the autopilot holds `key` down on the server (see pressKey). */
export function isAutoPilotHolding(key: KeyName): boolean {
  return isHeldKey(key) && heldKeys[key];
}

function releaseAllKeys(): void {
  pressKey('UP', false);
  pressKey('LEFT', false);
  pressKey('RIGHT', false);
  pressKey('SPECIAL', false);
}

function worldToGridX(worldX: number): number {
  return Math.floor((worldX + MAP_CENTER_X) / CELL_SIZE);
}

function worldToGridY(worldY: number): number {
  return Math.floor((worldY + MAP_CENTER_Y) / CELL_SIZE);
}

function gridToWorldX(gridX: number): number {
  return gridX * CELL_SIZE - MAP_CENTER_X + CELL_SIZE / 2;
}

function gridToWorldY(gridY: number): number {
  return gridY * CELL_SIZE - MAP_CENTER_Y + CELL_SIZE / 2;
}

/**
 * Lazily builds the Pathfinder + walkable grid by rasterizing the bounding box
 * of every wall (mountain) into blocked cells. Bounding-box (not exact circle)
 * matches SWAM and gives an implicit safety margin around obstacles.
 */
function ensurePathfinder(): Pathfinder {
  if (pathfinder !== null) return pathfinder;
  pathfinder = new Pathfinder(GRID_W, GRID_H);
  const blocked = pathfinder.blocked;
  for (const wall of walls) {
    const r = wall.radius;
    const x0 = worldToGridX(wall.x - r);
    const x1 = worldToGridX(wall.x + r);
    const y0 = worldToGridY(wall.y - r);
    const y1 = worldToGridY(wall.y + r);
    const xStart = Math.max(0, x0);
    const xEnd = Math.min(GRID_W - 1, x1);
    const yStart = Math.max(0, y0);
    const yEnd = Math.min(GRID_H - 1, y1);
    for (let y = yStart; y <= yEnd; y++) {
      for (let x = xStart; x <= xEnd; x++) {
        blocked[y * GRID_W + x] = 1;
      }
    }
  }

  // Penalize cells adjacent to walls so A* prefers a path one cell off the
  // mountain when there's room. Threading tight maze passages still works
  // because the penalty applies uniformly along all candidate routes.
  const penalty = new Float32Array(GRID_W * GRID_H);
  for (let y = 0; y < GRID_H; y++) {
    for (let x = 0; x < GRID_W; x++) {
      const idx = y * GRID_W + x;
      if (blocked[idx] === 1) continue;
      const xMin = Math.max(0, x - 1);
      const xMax = Math.min(GRID_W - 1, x + 1);
      const yMin = Math.max(0, y - 1);
      const yMax = Math.min(GRID_H - 1, y + 1);
      for (let ny = yMin; ny <= yMax; ny++) {
        for (let nx = xMin; nx <= xMax; nx++) {
          if (blocked[ny * GRID_W + nx] === 1) {
            penalty[idx] = NEAR_WALL_PENALTY;
            ny = yMax;
            break;
          }
        }
      }
    }
  }
  pathfinder.enterPenalty = penalty;
  return pathfinder;
}

/**
 * Searches square rings outward from (cx, cy) and returns the first clear
 * cell found, stopping at `maxRadius`. Result is nearest by Chebyshev
 * radius (not Euclidean) with scan-order tie-breaking. Used to snap a
 * blocked start or goal (e.g. user clicks on a mountain) to the nearest
 * legal cell so pathfinding can still succeed.
 */
function findNearestWalkable(
  pf: Pathfinder,
  cx: number,
  cy: number,
  maxRadius: number,
): { x: number; y: number } | null {
  if (cx >= 0 && cx < GRID_W && cy >= 0 && cy < GRID_H && pf.blocked[cy * GRID_W + cx] === 0) {
    return { x: cx, y: cy };
  }
  for (let r = 1; r <= maxRadius; r++) {
    for (let dy = -r; dy <= r; dy++) {
      const y = cy + dy;
      if (y < 0 || y >= GRID_H) continue;
      const onRowEdge = dy === -r || dy === r;
      const xStart = cx - r;
      const xEnd = cx + r;
      for (let x = xStart; x <= xEnd; x++) {
        if (x < 0 || x >= GRID_W) continue;
        if (!onRowEdge && x !== xStart && x !== xEnd) continue;
        if (pf.blocked[y * GRID_W + x] === 0) return { x, y };
      }
    }
  }
  return null;
}

/** Wraps `delta` into (-π, π], robust against accumulated rotation. */
function normalizeAngle(delta: number): number {
  const wrapped = ((((delta + Math.PI) % TWO_PI) + TWO_PI) % TWO_PI) - Math.PI;
  return wrapped === -Math.PI ? Math.PI : wrapped;
}

/** Angle from `from` to `to` in airmash convention (0 = north, increases CW). */
function angleTo(fromX: number, fromY: number, toX: number, toY: number): number {
  return Math.atan2(toX - fromX, fromY - toY);
}

function tick(): void {
  if (!active) return;
  const me = getMe();
  if (!me || me.status !== PLAYER_STATUS.ALIVE) {
    cancelAutoPilot();
    return;
  }

  // Scale the teleport threshold by actual elapsed time so a delayed tick
  // (background tab, GC pause) doesn't false-cancel during legitimate boost.
  const now = performance.now();
  const elapsedMs = prevTickTime === 0 ? TICK_MS : now - prevTickTime;
  prevTickTime = now;
  const allowed = TELEPORT_DELTA_PER_100MS * Math.max(1, elapsedMs / TICK_MS);
  const dx = me.pos.x - prevX;
  const dy = me.pos.y - prevY;
  if (dx * dx + dy * dy > allowed * allowed) {
    cancelAutoPilot();
    return;
  }
  prevX = me.pos.x;
  prevY = me.pos.y;

  if (path === null || path.length === 0) {
    cancelAutoPilot();
    return;
  }

  // Advance past waypoints we've already passed. Proximity (ARRIVAL_DIST) used
  // to be enough, but with look-ahead steering the plane cuts corners and may
  // never come within that radius. The dot-product test "have we crossed the
  // perpendicular line through this waypoint, facing onward?" handles both
  // cases: it triggers when we're at the node OR when we've sailed past it
  // laterally on the way to the next one.
  while (nodeIndex < path.length - 1) {
    const cur = path[nodeIndex]!;
    const cx = gridToWorldX(unpackX(cur));
    const cy = gridToWorldY(unpackY(cur));
    const ddx = me.pos.x - cx;
    const ddy = me.pos.y - cy;
    if (ddx * ddx + ddy * ddy <= ARRIVAL_DIST_SQ) {
      nodeIndex++;
      continue;
    }
    // Segment direction is the incoming leg (prev → cur). nodeIndex starts at
    // 1 (we skip the snapped start cell), so path[nodeIndex - 1] is always valid.
    const prev = path[nodeIndex - 1]!;
    const segDx = cx - gridToWorldX(unpackX(prev));
    const segDy = cy - gridToWorldY(unpackY(prev));
    // ddx,ddy points from cur → plane. If its component along seg-direction is
    // positive, the plane is past cur in the direction it was heading.
    if (ddx * segDx + ddy * segDy >= 0) {
      nodeIndex++;
      continue;
    }
    break;
  }

  const finalNode = path[path.length - 1]!;
  const finalX = gridToWorldX(unpackX(finalNode));
  const finalY = gridToWorldY(unpackY(finalNode));
  const finalDx = me.pos.x - finalX;
  const finalDy = me.pos.y - finalY;
  const finalDistSq = finalDx * finalDx + finalDy * finalDy;
  if (finalDistSq <= FINAL_ARRIVAL_DIST_SQ) {
    cancelAutoPilot();
    return;
  }

  // Pure-pursuit look-ahead: walk forward along the path from the plane's
  // current position, accumulating along-path distance, until we've consumed
  // LOOKAHEAD_DIST. Steer toward that point. Falls back to the final node if
  // the path is shorter than the look-ahead.
  let lookX = me.pos.x;
  let lookY = me.pos.y;
  let curX = me.pos.x;
  let curY = me.pos.y;
  let remaining = LOOKAHEAD_DIST;
  for (let i = nodeIndex; i < path.length; i++) {
    const nx = gridToWorldX(unpackX(path[i]!));
    const ny = gridToWorldY(unpackY(path[i]!));
    const segDx = nx - curX;
    const segDy = ny - curY;
    const segLen = Math.sqrt(segDx * segDx + segDy * segDy);
    if (segLen >= remaining) {
      const t = segLen > 0 ? remaining / segLen : 0;
      lookX = curX + segDx * t;
      lookY = curY + segDy * t;
      break;
    }
    remaining -= segLen;
    curX = nx;
    curY = ny;
    lookX = nx;
    lookY = ny;
  }

  const desired = angleTo(me.pos.x, me.pos.y, lookX, lookY);
  const delta = normalizeAngle(desired - me.rot);

  // Throttle is held continuously. Look-ahead steering smooths corners so
  // the plane never has to slow in place — sharp inputs become gentle banks.
  pressKey('UP', true);

  // Boost handling — independent of turn state. Gated on distance-to-final so
  // long trips boost regardless of look-ahead point position.
  if (boostUntil !== 0 && now >= boostUntil) {
    pressKey('SPECIAL', false);
    boostUntil = 0;
  }
  if (
    boostUntil === 0 &&
    me.type === PlaneType.Predator &&
    me.energy >= BOOST_ENERGY &&
    finalDistSq > BOOST_DIST_SQ &&
    Math.abs(delta) < TURN_THRESHOLD
  ) {
    pressKey('SPECIAL', true);
    boostUntil = now + BOOST_MS;
  }

  // Turn pulse — only issue a new one when the previous one (and its
  // cooldown) have completed, so we don't double-press and over-rotate.
  if (rotating) return;
  if (Math.abs(delta) <= TURN_THRESHOLD) return;

  // turnFactor is radians per frame at 60 fps, so 60 * turnFactor is radians per second
  const turnFactor = SHIPS[me.type].turnFactor;
  let durationMs = Math.round((Math.abs(delta) / (60 * turnFactor)) * 1000);
  if (durationMs < TURN_PULSE_MIN_MS) durationMs = TURN_PULSE_MIN_MS;
  if (durationMs > TURN_PULSE_MAX_MS) durationMs = TURN_PULSE_MAX_MS;

  const turnKey: HeldKey = delta > 0 ? 'RIGHT' : 'LEFT';
  const oppositeKey: HeldKey = delta > 0 ? 'LEFT' : 'RIGHT';
  pressKey(oppositeKey, false);
  pressKey(turnKey, true);
  rotating = true;

  turnReleaseTimerId = window.setTimeout(() => {
    turnReleaseTimerId = null;
    if (!active) return;
    pressKey('LEFT', false);
    pressKey('RIGHT', false);
    turnCooldownTimerId = window.setTimeout(() => {
      turnCooldownTimerId = null;
      if (!active) return;
      rotating = false;
    }, TURN_COOLDOWN_MS);
  }, durationMs);
}

/** Why a start was refused. */
export type AutoPilotFailure =
  | 'not alive'
  | 'start out of bounds'
  | 'destination out of bounds'
  | 'no walkable cell near start'
  | 'no walkable cell near destination'
  | 'no path found'
  | 'already at destination';

/** The outcome of a start: under way, or refused with the reason. */
export type AutoPilotStart = { ok: true } | { ok: false; reason: AutoPilotFailure };

/** Starts navigating to the world-space destination, or says why it cannot. */
export function startAutoPilot(worldX: number, worldY: number): AutoPilotStart {
  const me = getMe();
  if (!me || me.status !== PLAYER_STATUS.ALIVE) {
    return { ok: false, reason: 'not alive' };
  }

  const pf = ensurePathfinder();
  const rawSx = worldToGridX(me.pos.x);
  const rawSy = worldToGridY(me.pos.y);
  const rawTx = worldToGridX(worldX);
  const rawTy = worldToGridY(worldY);

  if (rawSx < 0 || rawSx >= GRID_W || rawSy < 0 || rawSy >= GRID_H) {
    return { ok: false, reason: 'start out of bounds' };
  }
  if (rawTx < 0 || rawTx >= GRID_W || rawTy < 0 || rawTy >= GRID_H) {
    return { ok: false, reason: 'destination out of bounds' };
  }

  // Snap start to the nearest walkable cell. Tight radius — the player
  // shouldn't normally be deep inside an obstacle.
  const start = findNearestWalkable(pf, rawSx, rawSy, 5);
  if (start === null) {
    return { ok: false, reason: 'no walkable cell near start' };
  }
  // Snap goal — generous radius so clicks on a mountain still produce a
  // sensible path to the nearest open space.
  const goal = findNearestWalkable(pf, rawTx, rawTy, 20);
  if (goal === null) {
    return { ok: false, reason: 'no walkable cell near destination' };
  }

  const raw = pf.findPath(start.x, start.y, goal.x, goal.y);
  if (raw === null) {
    return { ok: false, reason: 'no path found' };
  }
  const smoothed = pf.smoothPath(raw);
  if (smoothed.length <= 1) {
    return { ok: false, reason: 'already at destination' };
  }

  // If we were already running, fully reset the control subsystem before
  // swapping paths — otherwise a stale turn timer could fire mid-restart
  // and stomp `rotating` / pressed keys for the new run.
  if (active) {
    clearTurnTimers();
    releaseAllKeys();
  }

  path = smoothed;
  // Skip node 0 — it's the snapped start cell, conceptually the origin.
  // Steering toward our own current cell is wasted motion; the first real
  // waypoint is node 1.
  nodeIndex = 1;
  prevX = me.pos.x;
  prevY = me.pos.y;
  prevTickTime = 0;
  boostUntil = 0;
  active = true;
  uiState.autopilotActive = true;

  if (intervalId === null) {
    intervalId = window.setInterval(tick, TICK_MS);
  }
  return { ok: true };
}

/** Stops the autopilot, releases all keys, hides the banner. */
export function cancelAutoPilot(): void {
  // Set inactive FIRST so any timer callbacks already queued (or any
  // synchronous re-entry via sendKey side effects) bail on their
  // `if (!active) return` guard.
  active = false;
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  clearTurnTimers();
  // Release on any cancel — pressKey dedupes, so this is safe even if we
  // weren't actively pressing a given key, and it leaves down any key the
  // player's input still holds, so a manual press takes over cleanly.
  releaseAllKeys();
  path = null;
  nodeIndex = 0;
  prevTickTime = 0;
  boostUntil = 0;
  if (uiState.autopilotActive) uiState.autopilotActive = false;
}

/** The input layer reports each manual press here; `key` is its bind or key
 *  name. Any direction or special press takes over from the autopilot. FIRE
 *  doesn't, as in SWAM (which cancelled on the direction keys only). SPECIAL
 *  still does, since the autopilot holds it for the Predator's boost. */
export function cancelAutoPilotOnUserInput(key: BindName): void {
  if (!active || key === 'FIRE') return;
  cancelAutoPilot();
}

export function isAutoPilotActive(): boolean {
  return active;
}
